import {
  BookOpen,
  Building2,
  CalendarClock,
  CalendarRange,
  GraduationCap,
  Layers,
  type LucideIcon,
  Receipt,
  UserPlus,
  UsersRound,
} from 'lucide-react'
import type { SetupStepKey } from '@/features/platform/types'

export interface SetupStepMeta {
  title: string
  description: string
  href: string
  action: string
  icon: LucideIcon
}

/** What each set-up step asks for and where it is done (English keys, translated in "setup"). */
export const SETUP_STEPS: Record<SetupStepKey, SetupStepMeta> = {
  profile: {
    title: 'Complete the school profile',
    description: 'Address, phone and logo appear on receipts, report cards and the portals.',
    href: '/admin/settings/school',
    action: 'Edit the profile',
    icon: Building2,
  },
  academic_year: {
    title: 'Create the academic year',
    description: 'Its dates frame attendance, fees, exams and leave. Mark it as current.',
    href: '/admin/academic/years',
    action: 'Add a year',
    icon: CalendarRange,
  },
  classes: {
    title: 'Add classes and sections',
    description: 'Grades and their sections, with capacity and class teacher.',
    href: '/admin/academic/classes',
    action: 'Add classes',
    icon: Layers,
  },
  subjects: {
    title: 'Set the subjects of each class',
    description: 'Create the subjects, then choose each class’s curriculum and weekly periods.',
    href: '/admin/academic/subjects',
    action: 'Add subjects',
    icon: BookOpen,
  },
  staff: {
    title: 'Add the staff',
    description: 'Teachers and office staff; give them a login from their record.',
    href: '/admin/staff',
    action: 'Add staff',
    icon: UsersRound,
  },
  students: {
    title: 'Admit the students',
    description: 'One by one, or all at once from a CSV file.',
    href: '/admin/students',
    action: 'Admit students',
    icon: GraduationCap,
  },
  timetable: {
    title: 'Build the timetable',
    description: 'Periods, rooms and each section’s week; it decides who teaches what.',
    href: '/admin/timetable',
    action: 'Open the timetable',
    icon: CalendarClock,
  },
  fees: {
    title: 'Set up the fees',
    description: 'Fee categories and items, to generate invoices.',
    href: '/admin/finance/setup',
    action: 'Set up fees',
    icon: Receipt,
  },
  team: {
    title: 'Invite your team',
    description: 'Accounts for the office: accountant, librarian, receptionist…',
    href: '/admin/settings/users',
    action: 'Manage users',
    icon: UserPlus,
  },
}
